import { useState } from 'react';

import { dateTo_DD_MM_YYYY_dot } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { LuCalendar } from 'react-icons/lu';

import type { DateTimeInputProps } from './types';
import { Label } from '../../general/Label';
import { Popover, PopoverContent, PopoverTrigger } from '../../general/Popover';
import { COMPONENTS_NAMESPACE } from '../../locales';
import { cn } from '../../utils/cn';
import { DateTimePicker } from '../DateTimePicker';

export type { DateTimeInputProps } from './types';

const getInitialDraft = (value: Date | null, minDate?: Date) => {
    if (value) return value;
    const now = new Date();
    return minDate && minDate.getTime() > now.getTime() ? minDate : now;
};

/**
 * A form input for picking a date together with a time of day.
 *
 * Opens a {@link DateTimePicker} in a popover that keeps a draft and only calls `onChange` once the user accepts it.
 *
 * @param id - ID of the trigger button. Used for htmlFor association.
 * @param label - Label text for the input.
 * @param value - The selected date and time, or null to show the placeholder.
 * @param onChange - Callback fired with the accepted date and time.
 * @param minDate - Earliest selectable point in time; earlier days and months are disabled and an earlier draft cannot be accepted.
 * @param title - Optional heading rendered above the calendar.
 * @param acceptLabel - Label of the accept button. Defaults to the translated "Übernehmen".
 * @param minuteStep - Positive whole number of minutes added or subtracted by the +/- buttons. Defaults to 1.
 * @param placeholder - Placeholder text. Defaults to the translated "TT.MM.JJJJ, HH:MM".
 * @param error - Error message shown below the input.
 * @param disabled - Whether the input is disabled.
 * @param layout - Layout direction: "horizontal" (default) or "vertical".
 * @param className - Additional CSS classes for the container.
 * @param labelClassName - Additional CSS classes for the label.
 * @param inputClassName - Additional CSS classes for the trigger.
 *
 * @example
 * <DateTimeInput id="resubmission" label="Wiedervorlage" value={date} onChange={setDate} minDate={new Date()} />
 */
export const DateTimeInput: React.FC<DateTimeInputProps> = ({
    acceptLabel,
    className,
    disabled,
    error,
    id,
    inputClassName,
    label,
    labelClassName,
    layout = 'horizontal',
    minDate,
    minuteStep = 1,
    onChange,
    placeholder,
    title,
    value,
}) => {
    const { t } = useTranslation(COMPONENTS_NAMESPACE);
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<Date>(() => getInitialDraft(value, minDate));

    const isHorizontal = layout === 'horizontal';
    const isDraftBeforeMin = !!minDate && draft.getTime() < minDate.getTime();
    const errorId = `${id}-error`;

    const formatValue = (date: Date) =>
        t('dateTimeInput.valueFormat', {
            date: dateTo_DD_MM_YYYY_dot(date),
            time: `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`,
        });

    const handleOpenChange = (nextOpen: boolean) => {
        if (disabled) return;
        if (nextOpen) setDraft(getInitialDraft(value, minDate));
        setOpen(nextOpen);
    };

    const handleAccept = () => {
        if (isDraftBeforeMin) return;
        onChange(draft);
        setOpen(false);
    };

    return (
        <div
            className={cn(
                'flex gap-2.5 min-w-0',
                isHorizontal ? 'flex-row items-center justify-start' : 'flex-col items-start',
                className,
            )}
        >
            {label && (
                <Label
                    htmlFor={id}
                    className={cn(
                        isHorizontal ? 'w-[125px] shrink-0 justify-end' : 'w-full px-0 py-0 justify-start',
                        labelClassName,
                    )}
                >
                    {label}
                </Label>
            )}
            <div className={cn('flex flex-col min-w-0', isHorizontal ? 'flex-1' : 'w-full')}>
                <Popover open={open} onOpenChange={handleOpenChange}>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            id={id}
                            disabled={disabled}
                            aria-invalid={!!error}
                            aria-describedby={error ? errorId : undefined}
                            className={cn(
                                'h-8 w-full flex items-center pl-3 bg-white border rounded text-left',
                                error ? 'border-alert-error' : 'border-grey-dark',
                                'focus:outline-none focus:ring-2 focus:ring-blue-focus focus:ring-offset-1',
                                disabled ? 'cursor-not-allowed bg-surface-light opacity-50' : 'cursor-pointer',
                                inputClassName,
                            )}
                        >
                            <span
                                className={cn(
                                    'flex-1 min-w-0 truncate text-sm',
                                    value ? 'text-midnight' : 'text-placeholder-text',
                                )}
                            >
                                {value ? formatValue(value) : placeholder ?? t('dateTimeInput.placeholder')}
                            </span>
                            <span className="border-l border-grey-dark h-4/6" />
                            <span
                                className="flex items-center justify-center h-5 w-8 shrink-0 text-midnight"
                                aria-hidden="true"
                            >
                                <LuCalendar className="size-4" />
                            </span>
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        align="start"
                        className="w-auto p-0 border border-grey-dark"
                        onOpenAutoFocus={(event) => event.preventDefault()}
                    >
                        <DateTimePicker
                            className="px-6 pb-6"
                            value={draft}
                            onChange={setDraft}
                            onAccept={handleAccept}
                            minDate={minDate}
                            minuteStep={minuteStep}
                            title={title}
                            acceptLabel={acceptLabel}
                        />
                    </PopoverContent>
                </Popover>
                {error && (
                    <span id={errorId} className="text-[10px] leading-[1.46] text-alert-error mt-1">
                        {error}
                    </span>
                )}
            </div>
        </div>
    );
};
